import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Select, Spin } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { Button, Input } from '@crosscode/gravity-components'
import { Link } from 'react-router-dom'
import {
  fetchChannels,
  fetchScreenshot,
  fetchServerPosTerminals,
  fetchTrassirServersByStore,
} from '@/features/trassir/api'
import type { TrassirCamera, TrassirServer } from '@/features/trassir/types'
import type { TerminalCameraBinding } from './types'
import styles from './CashCameraPickerModal.module.css'

interface Props {
  open: boolean
  storeId: number
  terminalLabel?: string | number
  initial?: Partial<TerminalCameraBinding> | null
  onClose: () => void
  onSave: (binding: TerminalCameraBinding) => void | Promise<void>
}

function CameraThumb({
  serverId,
  guid,
  active,
  loadToken,
}: {
  serverId: number
  guid: string
  active?: boolean
  loadToken: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const urlRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    }
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el || !serverId || !guid) return

    let cancelled = false
    let observer: IntersectionObserver | null = null

    const load = async () => {
      setLoading(true)
      setFailed(false)
      try {
        const blob = await fetchScreenshot(serverId, guid)
        if (cancelled) return
        if (urlRef.current) URL.revokeObjectURL(urlRef.current)
        const next = URL.createObjectURL(blob)
        urlRef.current = next
        setUrl(next)
      } catch {
        if (!cancelled) {
          setFailed(true)
          setUrl(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (active) {
      void load()
      return () => {
        cancelled = true
      }
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer?.disconnect()
          void load()
        }
      },
      { rootMargin: '80px' },
    )
    observer.observe(el)

    return () => {
      cancelled = true
      observer?.disconnect()
    }
  }, [serverId, guid, active, loadToken])

  return (
    <div ref={ref} className={styles.thumb}>
      {url ? <img src={url} alt="" className={styles.thumbImg} /> : null}
      {!url ? (
        <div className={styles.thumbEmpty}>
          {loading ? '…' : failed ? 'нет кадра' : 'превью'}
        </div>
      ) : null}
    </div>
  )
}

export function CashCameraPickerModal({
  open,
  storeId,
  terminalLabel,
  initial,
  onClose,
  onSave,
}: Props) {
  const [servers, setServers] = useState<TrassirServer[]>([])
  const [serversLoading, setServersLoading] = useState(false)
  const [serverId, setServerId] = useState<number | null>(null)
  const [cameras, setCameras] = useState<TrassirCamera[]>([])
  const [camerasLoading, setCamerasLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null)
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [thumbToken, setThumbToken] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [posGuid, setPosGuid] = useState<string | null>(null)
  const [posName, setPosName] = useState<string | null>(null)
  const [posOptions, setPosOptions] = useState<{ value: string; label: string }[]>([])
  const previewUrlRef = useRef<string | null>(null)

  const revokePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }

  useEffect(() => {
    return () => revokePreview()
  }, [])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setError(null)
    setServersLoading(true)
    void fetchTrassirServersByStore(storeId)
      .then((list) => {
        setServers(list)
        const preferred =
          initial?.trassir_server_id &&
          list.some((s) => s.id === initial.trassir_server_id)
            ? Number(initial.trassir_server_id)
            : list.length === 1
              ? list[0].id
              : list[0]?.id ?? null
        setServerId(preferred)
        setSelectedGuid(initial?.trassir_channel_guid || null)
        setSelectedName(initial?.trassir_channel_name || null)
        setPosGuid(initial?.trassir_pos_guid || null)
        setPosName(initial?.trassir_pos_name || null)
      })
      .catch((e) => {
        setServers([])
        setServerId(null)
        setError(e instanceof Error ? e.message : 'Не удалось загрузить серверы TRASSIR')
      })
      .finally(() => setServersLoading(false))
  }, [open, storeId, initial?.trassir_server_id, initial?.trassir_channel_guid, initial?.trassir_channel_name, initial?.trassir_pos_guid, initial?.trassir_pos_name])

  useEffect(() => {
    if (!open || !serverId) {
      setPosOptions([])
      return
    }
    void fetchServerPosTerminals(serverId)
      .then((list) => {
        setPosOptions(
          list.map((t) => ({
            value: t.guid,
            label: `${t.name || t.guid} · ${t.events_count} соб.`,
          })),
        )
      })
      .catch(() => setPosOptions([]))
  }, [open, serverId])

  useEffect(() => {
    if (!open || !serverId) {
      setCameras([])
      return
    }
    setCamerasLoading(true)
    setError(null)
    void fetchChannels(serverId)
      .then((list) => {
        setCameras(list)
        if (selectedGuid) {
          const found = list.find((c) => c.guid === selectedGuid)
          if (found) setSelectedName(found.name)
        }
      })
      .catch((e) => {
        setCameras([])
        setError(e instanceof Error ? e.message : 'Не удалось загрузить камеры')
      })
      .finally(() => setCamerasLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serverId])

  const loadPreview = useCallback(async (sid: number, guid: string) => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const blob = await fetchScreenshot(sid, guid)
      revokePreview()
      const next = URL.createObjectURL(blob)
      previewUrlRef.current = next
      setPreviewUrl(next)
    } catch {
      revokePreview()
      setPreviewUrl(null)
      setPreviewError('Не удалось получить кадр')
    } finally {
      setPreviewLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open || !serverId || !selectedGuid) {
      revokePreview()
      setPreviewUrl(null)
      setPreviewError(null)
      return
    }
    void loadPreview(serverId, selectedGuid)
  }, [open, serverId, selectedGuid, loadPreview])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cameras
    return cameras.filter(
      (c) => c.name.toLowerCase().includes(q) || c.guid.toLowerCase().includes(q),
    )
  }, [cameras, query])

  const selectCamera = (cam: TrassirCamera) => {
    setSelectedGuid(cam.guid)
    setSelectedName(cam.name)
  }

  const handleSave = async () => {
    if (!serverId || !selectedGuid) return
    setSaving(true)
    try {
      await onSave({
        trassir_server_id: serverId,
        trassir_channel_guid: selectedGuid,
        trassir_channel_name: selectedName || selectedGuid,
        trassir_pos_guid: posGuid,
        trassir_pos_name: posName,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    setSaving(true)
    try {
      await onSave({
        trassir_server_id: null,
        trassir_channel_guid: null,
        trassir_channel_name: null,
        trassir_pos_guid: null,
        trassir_pos_name: null,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сбросить')
    } finally {
      setSaving(false)
    }
  }

  const refreshFrames = () => {
    setThumbToken((n) => n + 1)
    if (serverId && selectedGuid) void loadPreview(serverId, selectedGuid)
  }

  const title = terminalLabel != null ? `Камера для кассы №${terminalLabel}` : 'Камера кассы'

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={720}
      footer={null}
      destroyOnHidden
    >
      <div className={styles.body}>
        {serversLoading ? (
          <div className={styles.emptyState}>
            <Spin />
          </div>
        ) : servers.length === 0 ? (
          <div className={styles.emptyState}>
            Для магазина нет серверов TRASSIR.
            <br />
            <Link to="/settings">Добавьте сервер в настройках</Link>
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              {servers.length > 1 ? (
                <Select
                  className={styles.serverSelect}
                  value={serverId ?? undefined}
                  options={servers.map((s) => ({
                    value: s.id,
                    label: `${s.ip}${s.port ? `:${s.port}` : ''}${s.store_name ? ` · ${s.store_name}` : ''}`,
                  }))}
                  onChange={(v) => {
                    setServerId(v)
                    setSelectedGuid(null)
                    setSelectedName(null)
                    setPosGuid(null)
                    setPosName(null)
                  }}
                  placeholder="Сервер TRASSIR"
                />
              ) : null}
              <Input
                className={styles.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск камеры…"
                allowClear
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={refreshFrames}
                disabled={!serverId}
              >
                Обновить кадры
              </Button>
            </div>

            {error ? <p className={styles.error}>{error}</p> : null}

            <div className={styles.layout}>
              <div className={styles.gridWrap}>
                {camerasLoading ? (
                  <div className={styles.emptyState}>
                    <Spin />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className={styles.emptyState}>Камеры не найдены</div>
                ) : (
                  <div className={styles.grid}>
                    {filtered.map((cam) => {
                      const active = cam.guid === selectedGuid
                      return (
                        <button
                          key={cam.guid}
                          type="button"
                          className={`${styles.card} ${active ? styles.cardActive : ''}`}
                          onClick={() => selectCamera(cam)}
                        >
                          <CameraThumb
                            serverId={serverId!}
                            guid={cam.guid}
                            active={active}
                            loadToken={thumbToken}
                          />
                          <span className={styles.cardName}>{cam.name}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <aside className={styles.preview}>
                <div className={styles.previewHead}>
                  <h3 className={styles.previewTitle}>
                    {selectedName || (selectedGuid ? 'Превью' : 'Выберите камеру')}
                  </h3>
                  <Button
                    size="small"
                    type="text"
                    icon={<ReloadOutlined />}
                    disabled={!selectedGuid || previewLoading}
                    onClick={() => {
                      if (serverId && selectedGuid) void loadPreview(serverId, selectedGuid)
                    }}
                  >
                    Кадр
                  </Button>
                </div>
                <div className={styles.previewFrame}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className={styles.previewImg} />
                  ) : (
                    <div className={styles.previewEmpty}>
                      {previewLoading
                        ? 'Загрузка…'
                        : previewError || 'Выберите камеру, чтобы увидеть превью'}
                    </div>
                  )}
                </div>
                {selectedGuid ? (
                  <p className={styles.muted}>{selectedGuid}</p>
                ) : null}
                <div className={styles.posBlock}>
                  <div className={styles.posLabel}>Терминал ActivePOS</div>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Авто по имени / выберите GUID"
                    className={styles.posSelect}
                    value={posGuid ?? undefined}
                    options={
                      posOptions.length
                        ? posOptions
                        : posGuid
                          ? [{ value: posGuid, label: posName || posGuid }]
                          : []
                    }
                    onChange={(v, opt) => {
                      setPosGuid(v ?? null)
                      const label =
                        opt && !Array.isArray(opt) && typeof opt.label === 'string'
                          ? opt.label.split(' · ')[0]
                          : null
                      setPosName(label)
                    }}
                    notFoundContent="Пока нет событий — сначала пробейте чек"
                  />
                  <Input
                    className={styles.posInput}
                    value={posGuid || ''}
                    onChange={(e) => {
                      const v = e.target.value.trim()
                      setPosGuid(v || null)
                      if (!v) setPosName(null)
                    }}
                    placeholder="Или введите GUID вручную"
                    allowClear
                  />
                </div>
              </aside>
            </div>

            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <Button onClick={() => void handleClear()} disabled={saving}>
                  Сбросить
                </Button>
              </div>
              <Button onClick={onClose} disabled={saving}>
                Отмена
              </Button>
              <Button
                type="primary"
                onClick={() => void handleSave()}
                disabled={saving || !serverId || !selectedGuid}
              >
                Сохранить
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
